import { ISaveBatteryInputData } from '@/interfaces/storageForecasting/IBatteryInputData.ts';
import { IAssetStorecastData, ISaveAssetInputData } from '@/interfaces/storageForecasting/IAsset.ts';
import { TStorageForecastCountries } from '@/types/TStorageForecastCountries.ts';

function withCountryFirst<T extends { country: TStorageForecastCountries }> (inputData: T): T {
    const { country, ...rest } = inputData;
    return { country, ...rest } as T;
}

interface IDegradationProfilePayload {
    averageCycles: number;
    degradationProfile: {
        values: number[];
        granularity: string;
        length: number;
    };
}

interface IDegradationPayload {
    values: number[];
    granularity: string;
    length: number;
}

function toDynamicProfilesForSave (
    value: unknown,
    options?: { efficiencyToDecimal?: boolean },
): IDegradationProfilePayload[] | null {
    if (!value || typeof value !== 'object') return null;

    const input = value as {
        degradationMode?: 'TimeBased' | 'DynamicCycles';
        granularity?: string;
        initialValue?: number;
        cycleSeries?: Array<{ numberOfCycles: number; values: number[] }>;
    };

    if (input.degradationMode !== 'DynamicCycles' || !Array.isArray(input.cycleSeries) || input.cycleSeries.length === 0) {
        return null;
    }

    const granularity = input.granularity ?? 'Year';
    const initialValueRaw = typeof input.initialValue === 'number' ? input.initialValue : 0;
    const initialValue = options?.efficiencyToDecimal ? (initialValueRaw / 100) : initialValueRaw;

    return input.cycleSeries.map((cycle) => {
        const tailValues = Array.isArray(cycle.values) ? cycle.values : [];
        const scaledTail = options?.efficiencyToDecimal
            ? tailValues.map((v) => v / 100)
            : [...tailValues];
        const values = [initialValue, ...scaledTail];
        return {
            averageCycles: cycle.numberOfCycles,
            degradationProfile: {
                values,
                granularity,
                length: values.length,
            },
        };
    });
}

function toStandardDegradationForSave (
    value: unknown,
    options?: { efficiencyToDecimal?: boolean },
): IDegradationPayload | null {
    if (!value || typeof value !== 'object') return null;

    const input = value as {
        values?: number[];
        granularity?: string;
    };

    if (!Array.isArray(input.values)) return null;

    const mappedValues = options?.efficiencyToDecimal
        ? input.values.map((v) => v / 100)
        : [...input.values];

    return {
        values: mappedValues,
        granularity: input.granularity ?? 'Year',
        length: mappedValues.length,
    };
}

export function canonicaliseSaveBatteryInputData (payload: ISaveBatteryInputData): ISaveBatteryInputData {
    const durationProfiles = toDynamicProfilesForSave(payload.data.duration);
    const efficiencyProfiles = toDynamicProfilesForSave(payload.data.efficiency, { efficiencyToDecimal: true });
    const durationDegradation = toStandardDegradationForSave(payload.data.duration);

    const dataWithCanonicalDynamic = {
        ...payload.data,
        duration: (durationProfiles ?? durationDegradation ?? payload.data.duration) as unknown,
        efficiency: (efficiencyProfiles ?? payload.data.efficiency) as unknown,
    };

    return {
        id: payload.id,
        type: payload.type,
        data: withCountryFirst(dataWithCanonicalDynamic) as ISaveBatteryInputData['data'],
    };
}

export function canonicaliseSaveAssetInputData (payload: ISaveAssetInputData): ISaveAssetInputData {
    return {
        id: payload.id,
        type: payload.type,
        data: withCountryFirst<IAssetStorecastData>(payload.data),
    };
}
